// Zavření obrazovky „tam, odkud se otevřela“ (nález CL.16: „Když zadávám zakázku z kalendáře a pak ji zavřu, tak se
// objevím v zakázkách místo zpět v kalendáři“). Křížek je návrat, ne odkaz na seznam: má‑li aplikace v historii
// předchozí stránku, vrátí se na ni (Kalendář → Nová zakázka → karta → × = Kalendář, protože karta po založení
// nahrazuje formulář); otevřená rovnou (odkaz, nová záložka) jde na výchozí seznam. Drobeček nahoře zůstává odkazem
// na seznam — ukazuje, kde obrazovka v aplikaci leží, ne odkud člověk přišel.
import { useEffect, useLayoutEffect, useRef, type MouseEvent } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router';

/** Funkce „zavřít“: zpět v historii aplikace, jinak na `vychozi`. */
export function useZpetNebo(vychozi: string): () => void {
  const navigate = useNavigate();
  const location = useLocation();
  // `default` = první stránka relace (otevřeno odkazem nebo v nové záložce) — zpět by opustilo aplikaci.
  const maHistorii = location.key !== 'default';
  return () => {
    if (maHistorii) void navigate(-1);
    else void navigate(vychozi);
  };
}

/** Jak člověk na okno přišel — zapamatuje se v okamžiku, kdy se okno v adrese objevilo. */
interface Prichod {
  /** Cíl, který poslal odkaz (`state.zpet` / `?zpet=`). */
  zpet: string | null;
  /** Okno otevřel odkaz, který přidal záznam do historie (Link, `navigate`) — zavření = krok zpět. */
  krokZpet: boolean;
}

/** Formulář, který po uložení nahradil sám sebe kartou (`replace`), předá „přišel jsem odkazem“ ve `state.krokZpet`. */
const krokZeStavu = (stav: unknown) =>
  Boolean(stav && typeof stav === 'object' && (stav as { krokZpet?: unknown }).krokZpet === true);

/** Cesta uvnitř aplikace (ne `//jiny-web` ani celá adresa) — jinak null. */
export function bezpecnaCestaAplikace(h: unknown): string | null {
  return typeof h === 'string' && h.startsWith('/') && !h.startsWith('//') && !h.startsWith('/\\')
    ? h
    : null;
}

/** Parametr, kterým se Nová zakázka vrací na `zpet` s právě založenou zakázkou (vyúčtování paušálů, 05/3887). */
export const PARAM_NOVA_ZAKAZKA = 'nova_zakazka';

/** Adresa `zpet` doplněná o `nova_zakazka=<id>` — okno, které odkaz poslalo, zakázku předvybere. */
export function adresaNavratuSNovouZakazkou(zpet: string, zakazkaId: string): string {
  const [cesta = '', dotaz = ''] = zpet.split('?', 2);
  const q = new URLSearchParams(dotaz);
  q.set(PARAM_NOVA_ZAKAZKA, zakazkaId);
  return `${cesta}?${q.toString()}`;
}

/** Cesta uvnitř aplikace z `state.zpet`, jinak z `?zpet=`. */
function cilOdkazu(stav: unknown, hledani: string): string | null {
  const zeStavu =
    stav && typeof stav === 'object'
      ? bezpecnaCestaAplikace((stav as { zpet?: unknown }).zpet)
      : null;
  return zeStavu ?? bezpecnaCestaAplikace(new URLSearchParams(hledani).get('zpet'));
}

const KLIC_ULOZISTE = 'ecoser-prichod-oken';
const MAX_ZAZNAMU = 100;

/** Příchod okna uložený k záznamu historie (přežije krok zpět i obnovení stránky v téže záložce). */
function nactiPrichod(klic: string): Prichod | null {
  try {
    const mapa = JSON.parse(sessionStorage.getItem(KLIC_ULOZISTE) ?? '{}') as Record<
      string,
      Prichod
    >;
    return mapa[klic] ?? null;
  } catch {
    return null;
  }
}

function ulozitPrichod(klic: string, prichod: Prichod): void {
  if (!prichod.zpet && !prichod.krokZpet) return;
  try {
    const mapa = JSON.parse(sessionStorage.getItem(KLIC_ULOZISTE) ?? '{}') as Record<
      string,
      Prichod
    >;
    delete mapa[klic];
    mapa[klic] = prichod;
    const klice = Object.keys(mapa);
    for (const k of klice.slice(0, Math.max(0, klice.length - MAX_ZAZNAMU))) delete mapa[k];
    sessionStorage.setItem(KLIC_ULOZISTE, JSON.stringify(mapa));
  } catch {
    // Bez úložiště (soukromé okno) se jen nepamatuje návrat krokem zpět.
  }
}

/**
 * Zavření okna (šuplík, karta, detail) „tam, odkud se otevřelo“ — pravidlo pro celou aplikaci (připomínka zadavatele
 * 8. 10. 2026: „po zavření okna ať jsem tam, odkud jsem přišel“; dřív CL.16 u karty zakázky).
 *
 * `otevreno` = okno je teď v adrese (parametr `?detail=…` nebo cesta karty). Hook si zapamatuje, jak se tam
 * dostalo: odkaz odjinud (karta zákazníka, zvoneček, hledání, signál, Co hoří…) přidá záznam do historie
 * (PUSH) → zavření udělá krok zpět; odkaz s cílem `zpet` → tam. Okno, které otevřela sama sekce bez nového
 * záznamu (replace, jen stav komponenty) nebo otevřené rovnou (nová záložka, e‑mail, obnovení stránky), se zavře
 * po svém — funkcí `mistne` (smazat parametr, seznam sekce).
 */
export function useZavritZpet(otevreno: boolean): (mistne: () => void) => void {
  const navigate = useNavigate();
  const umisteni = useLocation();
  const typ = useNavigationType();
  const prichod = useRef<Prichod | null>(null);
  // Adresa, na které se okno naposledy zavřelo: okno znovu otevřené na téže adrese (jen stav komponenty, třeba
  // další lístek z téhož seznamu) už odkazem nepřišlo.
  const zavrenoNa = useRef<string | null>(null);
  useEffect(() => {
    if (!otevreno) {
      prichod.current = null;
      return;
    }
    if (!prichod.current) {
      // Návrat krokem zpět (POP) na okno, které už jednou odkazem přišlo: příchod si pamatuje záznam historie.
      const ulozeny = typ === 'POP' ? nactiPrichod(umisteni.key) : null;
      prichod.current =
        ulozeny ??
        (zavrenoNa.current === umisteni.key
          ? { zpet: null, krokZpet: false }
          : {
              zpet: cilOdkazu(umisteni.state, umisteni.search),
              krokZpet: typ === 'PUSH' || krokZeStavu(umisteni.state),
            });
    }
    // I po přepnutí záložky okna (replace = nový klíč záznamu).
    ulozitPrichod(umisteni.key, prichod.current);
  }, [otevreno, umisteni.key, umisteni.state, umisteni.search, typ]);
  return (mistne) => {
    const p = prichod.current;
    prichod.current = null;
    zavrenoNa.current = umisteni.key;
    if (p?.zpet) void navigate(p.zpet);
    else if (p?.krokZpet) void navigate(-1);
    else mistne();
  };
}

// ---------------------------------------------------------------------------------------------
// Sledování historie aplikace: ke každému záznamu historie (klíč umístění) cesta a záznam, ze kterého se na něj
// přišlo odkazem. Přepnutí bez nového záznamu (replace — přesměrování, úklid parametrů, záložka) předchůdce zdědí.
// Slouží obrazovkám, které mají víc kroků v historii (Můj den › jízda) nebo si parametr odkazu hned uklidí
// (provozní deník, předvyplněná srážka): zavření ví, o kolik kroků zpět leží místo, odkud člověk přišel.

const KLIC_HISTORIE = 'ecoser-historie-oken';
const MAX_HISTORIE = 300;

interface ZaznamHistorie {
  cesta: string;
  /** Klíč záznamu, ze kterého se sem přišlo odkazem; bez něj = otevřeno rovnou (nebo neznámo). */
  pred?: string;
}

function nactiHistorii(): Record<string, ZaznamHistorie> {
  try {
    return JSON.parse(sessionStorage.getItem(KLIC_HISTORIE) ?? '{}') as Record<
      string,
      ZaznamHistorie
    >;
  } catch {
    return {};
  }
}

let posledniKlic: string | null = null;

/**
 * Zapisuje historii aplikace; vykresluje se jednou v kořeni cest (`CestyAplikace`).
 *
 * Zápis běží v `useLayoutEffect` — ještě v commitu, kterým se nová adresa objevila. Router vykresluje navigaci
 * v přechodu (startTransition) a obyčejný `useEffect` po takovém commitu React spouští až v další úloze: klepnutí,
 * které přišlo mezi tím (pomalý tablet; na vytíženém CI test W133), četlo v `krokuZpetOdkud` historii bez záznamu
 * právě zobrazené adresy, zavřelo jen zásuvku a člověk zůstal na obrazovce místo návratu tam, odkud přišel.
 */
export function SledovaniHistorie(): null {
  const umisteni = useLocation();
  const typ = useNavigationType();
  useLayoutEffect(() => {
    const klic = umisteni.key;
    if (klic === posledniKlic) return;
    const mapa = nactiHistorii();
    const cesta = `${umisteni.pathname}${umisteni.search}`;
    const predchozi = posledniKlic;
    if (typ === 'PUSH' && predchozi) mapa[klic] = { cesta, pred: predchozi };
    else if (typ === 'REPLACE' && predchozi) mapa[klic] = { cesta, pred: mapa[predchozi]?.pred };
    else mapa[klic] = { ...mapa[klic], cesta };
    posledniKlic = klic;
    try {
      const klice = Object.keys(mapa);
      for (const k of klice.slice(0, Math.max(0, klice.length - MAX_HISTORIE))) delete mapa[k];
      sessionStorage.setItem(KLIC_HISTORIE, JSON.stringify(mapa));
    } catch {
      // Bez úložiště se zavře „po svém“.
    }
  }, [umisteni.key, umisteni.pathname, umisteni.search, typ]);
  return null;
}

/**
 * O kolik kroků zpět leží místo, odkud člověk na obrazovku přišel: přeskočí záznamy, které k obrazovce patří
 * (`patri` — třeba další kroky téže jízdy). `null` = obrazovka otevřená rovnou (nová záložka, e‑mail) nebo neznámo.
 */
export function krokuZpetOdkud(
  klic: string,
  patri: (cesta: string) => boolean = () => false,
): number | null {
  const mapa = nactiHistorii();
  let kroku = 1;
  let pred = mapa[klic]?.pred;
  while (pred && mapa[pred] && patri(mapa[pred].cesta)) {
    kroku += 1;
    pred = mapa[pred].pred;
  }
  return pred && mapa[pred] ? kroku : null;
}

/** Test: zapomenout historii mezi testy (modul drží poslední klíč). */
export function zapomenoutHistorii(): void {
  posledniKlic = null;
  try {
    sessionStorage.removeItem(KLIC_HISTORIE);
  } catch {
    // nic
  }
}

/**
 * Obsluha odkazu „Zavřít“ / „Zpět“ celostránkové obrazovky s víc záznamy v historii (záložky karty, kroky jízdy):
 * vrátí tam, odkud člověk na obrazovku přišel — záznamy, které k ní patří (`patri`), přeskočí; otevřená rovnou
 * → `vychozi`. Odkaz sám vede na `vychozi` (nová záložka, prostřední tlačítko myši dál fungují).
 */
export function useZpetZObrazovky(
  patri: (cesta: string) => boolean,
  vychozi: string,
): (u: MouseEvent) => void {
  const navigate = useNavigate();
  const { key } = useLocation();
  return (u) => {
    if (u.metaKey || u.ctrlKey || u.shiftKey || u.button !== 0) return;
    u.preventDefault();
    const kroku = krokuZpetOdkud(key, patri);
    if (kroku) void navigate(-kroku);
    else void navigate(vychozi);
  };
}

/** Patří cesta k obrazovce se základem `zaklad` (`/zamestnanci/123`, i s podcestou nebo parametry)? */
export const podCestou = (zaklad: string) => (cesta: string) =>
  cesta === zaklad || cesta.startsWith(`${zaklad}/`) || cesta.startsWith(`${zaklad}?`);
